import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import SEO from '@/components/seo'
import type {InvoicesApprovalsQuery} from '@/graphql-types'
import useParams from '@/hooks/use-params'
import InvoicesApprovalsTemplate from '@/templates/invoices-approval/invoices-approvals-template'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'

type Props = PageProps<InvoicesApprovalsQuery>

export enum InvoiceStatus {
    Approved = 'APPROVED',
    Rejected = 'REJECTED',
}

const InvoicesApprovals = (props: Props) => {
    const content = props.data.allPrismicInvoicesApprovals.nodes[0]?.data
    const params = useParams()

    if (!content) {
        return handleNoPrismicData()
    }

    const invoiceId = params.get('ii')
    const approverId = params.get('ai')
    const token = params.get('t')
    const action = params.get('a')

    return (
        <InvoicesApprovalsTemplate
            invoiceId={invoiceId}
            approverId={approverId}
            token={token}
            action={
                action === InvoiceStatus.Approved || action === InvoiceStatus.Rejected
                    ? action
                    : null
            }
            content={content}
        />
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<InvoicesApprovalsQuery>) => {
    const pageData = data.allPrismicInvoicesApprovals.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
            preventIndexing={!pageData.allow_indexing}
        />
    )
}

export const query = graphql`
    query InvoicesApprovals($locale: String!) {
        allPrismicInvoicesApprovals(filter: {lang: {eq: $locale}}) {
            nodes {
                data {
                    allow_indexing
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    headerCta: header_cta
                    headerCtaLink: header_cta_link {
                        ...PrismicLink
                    }
                    headerLink: header_link {
                        richText
                    }
                    pageCtaText: page_cta_text
                    pageCtaLink: page_cta_link {
                        ...PrismicLink
                    }
                    approvalSuccess: approval_success {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title: approval_success_title
                        approval_success_text {
                            richText
                        }
                    }
                    rejectionSuccess: rejection_success {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title: rejection_success_title
                        rejection_success_text {
                            richText
                        }
                    }
                    errorProcessingRequest: error_processing_request {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title: error_processing_request_title
                        error_processing_request_text {
                            richText
                        }
                    }
                }
            }
        }
    }
`

export default InvoicesApprovals
